import { Toast, type ToastManager, type ToastManagerAddOptions } from '@base-ui/react/toast';
import i18n from 'i18next';
import type { ReactNode } from 'react';
import { prefersMoreContrast } from '~/hooks/use-more-contrast';
import type { ToastData } from '~/modules/ui/toast';

/** Toast variants with their own icon. */
export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

/** Base UI toast options; the message and severity come from the call. */
export type ToastOptions = Omit<ToastManagerAddOptions<ToastData>, 'title' | 'type'>;

// Errors and toasts with a description carry more to read, so they stay longer
const timeout = 4_000;
const longTimeout = 10_000;

const manager = Toast.createToastManager<ToastData>();
let held: ToastManagerAddOptions<ToastData>[] = [];
let subscriberCount = 0;
let frontmostId: string | undefined;

/**
 * The manager the app's `Toaster` renders. Toasts shown before any `Toaster` subscribes (a route `beforeLoad` on
 * first load, for instance) are held and replayed to the first subscriber, where a bare manager would drop them.
 */
export const toastManager: ToastManager<ToastData> = {
  ...manager,
  ' subscribe': (listener) => {
    const unsubscribe = manager[' subscribe'](listener);
    subscriberCount += 1;
    for (const options of held.splice(0)) manager.add(options);
    return () => {
      subscriberCount -= 1;
      unsubscribe();
    };
  },
};

function add(options: ToastManagerAddOptions<ToastData>) {
  if (subscriberCount > 0) return manager.add(options);
  const id = options.id ?? crypto.randomUUID();
  held.push({ ...options, id });
  return id;
}

function close(id?: string) {
  if (subscriberCount > 0) return manager.close(id);
  held = id === undefined ? [] : held.filter((options) => options.id !== id);
}

/**
 * How long a toast stays, in ms; 0 keeps it until dismissed. Increased contrast overrules every timeout: a reader who
 * needs stronger edges likely needs more than a few seconds as well. A toast that asks for a decision passes
 * `timeout: 0` itself; the rest close by themselves.
 */
function timeoutFor(type: ToastSeverity | undefined, options: ToastOptions) {
  if (prefersMoreContrast()) return 0;
  return options.timeout ?? (type === 'error' || options.description ? longTimeout : timeout);
}

/**
 * A string message gets a stable id, so repeating it shows one toast. Base UI updates a known id where it stands, under
 * the toasts added since: a repeat that is covered closes first, so its add lands on top. An explicit id is the caller
 * updating its own toast, which stays in place and keeps its `onClose` unfired. Errors are announced urgently.
 */
function show(type?: ToastSeverity) {
  return (message: ReactNode, options: ToastOptions = {}) => {
    const id = options.id ?? (typeof message === 'string' ? `toast:${message}` : undefined);
    if (!options.id && id && id !== frontmostId) close(id);
    frontmostId = add({ priority: type === 'error' ? 'high' : 'low', ...options, id, title: message, type, timeout: timeoutFor(type, options) });
    return frontmostId;
  };
}

/** Shows a toast and returns its id: `toaster(message)` plain, `toaster.<severity>(message)` with an icon. */
export const toaster = Object.assign(show(), { success: show('success'), info: show('info'), warning: show('warning'), error: show('error'), close });

/** Warns that the action needs a connection. */
export const toastOffline = () => toaster.warning(i18n.t('c:action.offline.text'));
